/**
 * Cliente del Agente TA Metrics contra su Function URL directa
 * (VITE_TA_ASK_URL) — sin CloudFront, sin su timeout de 30 s: un turno
 * analítico encadena varias llamadas a Sonnet con herramientas. Mismo patrón
 * que dtalk-api.ts. Si la env no está definida (stage sin redeploy de infra),
 * el caller cae al procedure tRPC taMetrics.ask.
 */
import type { AppLang } from '@/../../shared/lang';
import type { TaTipoConsulta } from '@/../../shared/ta-consulta';
import type { TaIntakeTurnInput, TaIntakeTurnResult } from '@/../../shared/types/ta-intake';
import type { JdRef } from '@/../../shared/types/ta-interview';
import { fetchAuthSession } from 'aws-amplify/auth';
import { fetchWithSessionGuard } from './session-expired';

export interface TaAskInput {
  question: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  /** Idioma de la conversación (el de la pantalla). Sin esto, el agente contesta en español. */
  lang?: AppLang;
  /** 'mine': solo las búsquedas de quien pregunta (apartado de intake). Por defecto, la base entera. */
  scope?: 'all' | 'mine';
}

/** Adjunto que la plataforma dibuja como botón (el modelo nunca escribe links). */
export interface TaAskAttachment {
  kind: 'ta_report';
  ID: string;
  posicion: string;
  candidatos: number;
}

export interface TaAskResult {
  reply: string;
  attachment?: TaAskAttachment;
  toolCalls: Array<{ name: string; input: Record<string, unknown> }>;
  dataset: { filas: number; downloadedAt: string; hoy: string };
  validation: { ok: boolean; issues: string[]; regenerated: boolean };
  clasificacion?: { tipo: TaTipoConsulta; refresh: boolean; cantidad: number | null; limites: string[] };
  latencyMs: number;
}

export const TA_ASK_URL: string | undefined = import.meta.env.VITE_TA_ASK_URL;

/**
 * El POST a la Function URL de los agentes de TA. Los tres agentes comparten
 * endpoint y se distinguen por el campo `agent` del cuerpo; lo único propio de
 * cada uno es el cuerpo y cómo se llama en el mensaje de error.
 */
async function postAlAgenteTa<T>(body: unknown, label: string): Promise<T> {
  if (!TA_ASK_URL) throw new Error('VITE_TA_ASK_URL no configurada');
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  const res = await fetchWithSessionGuard(TA_ASK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error ?? ''; } catch { /* sin cuerpo */ }
    throw new Error(detail || `${label}: error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function askTaAgentDirect(input: TaAskInput): Promise<TaAskResult> {
  return postAlAgenteTa<TaAskResult>(input, 'TA agent');
}

/**
 * Hiring Manager Intake — un turno del agente por la MISMA Function URL
 * (`agent: 'intake'`): generar la JD en dos idiomas o la Sourcing Guide tarda
 * más que los 30 s de CloudFront. Sin la env, el caller cae a taIntake.turn.
 * Los errores de estado (409 versión vencida, 412 conversación cerrada) llegan
 * con su mensaje para mostrarlo tal cual.
 */
export async function intakeTurnDirect(input: { sessionId: string; input: TaIntakeTurnInput; lang?: AppLang }): Promise<TaIntakeTurnResult> {
  return postAlAgenteTa<TaIntakeTurnResult>({ agent: 'intake', ...input }, 'Intake agent');
}

export interface InterviewPlanStarted {
  planId: string;
  status: 'GENERATING';
}

/**
 * Interview Plan — PIDE la guía y vuelve enseguida con el id.
 *
 * La generación corre en segundo plano porque tarda de 2 a 4 minutos (medido
 * contra el modelo: ~110 s un intento, y puede haber uno más). Colgar el
 * navegador de una conexión tanto tiempo es frágil —basta que la máquina se
 * suspenda— así que la pantalla consulta el estado hasta que esté lista.
 */
export async function interviewPlanDirect(jd: JdRef): Promise<InterviewPlanStarted> {
  return postAlAgenteTa<InterviewPlanStarted>({ agent: 'interview', jd }, 'Interview Plan agent');
}
